/**
 * ExperienceCard molecule — single experience entry with inline edit.
 *
 * Read mode: role (body-md semibold), company (body-sm), date range (body-sm secondary),
 * description (body-sm), icon-only edit + delete Buttons.
 * Edit mode: inline form with role/company/dates/description inputs,
 * Save and "Discard changes" action buttons.
 *
 * All icon-only buttons have a title prop set to the corresponding i18n key.
 * No hardcoded strings, no gradients, no emojis.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Trash2, Building2, Calendar } from 'lucide-react';
import { Typography } from '@/components/ui/atoms/Typography';
import { Button } from '@/components/ui/atoms/Button';
import { Input } from '@/components/ui/atoms/Input';
import { Textarea } from '@/components/ui/textarea';
import { DatePicker } from '@/components/ui/date-picker';
import type { TalentExperience } from '@/types/talent.types';

export interface ExperienceCardProps {
  experience: TalentExperience;
  onSave?: (updated: TalentExperience) => void;
  onDelete?: (id: string) => void;
  /** Start in edit mode (for newly created entries) */
  initialEditing?: boolean;
}

function formatDateRange(
  ongoingLabel: string,
  startDate: string,
  endDate?: string,
  isOngoing?: boolean,
): string {
  const start = new Date(startDate).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
  });
  if (isOngoing) return `${start} — ${ongoingLabel}`;
  if (!endDate) return start;
  const end = new Date(endDate).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
  });
  return `${start} — ${end}`;
}

export const ExperienceCard: React.FC<ExperienceCardProps> = ({
  experience,
  onSave,
  onDelete,
  initialEditing = false,
}) => {
  const { t } = useTranslation('talentPool');
  const [isEditing, setIsEditing] = useState(initialEditing);
  const [form, setForm] = useState<TalentExperience>(experience);

  const handleSave = () => {
    onSave?.(form);
    setIsEditing(false);
  };

  const handleDiscard = () => {
    // Newly added (never-saved) cards are temporary UI state — remove them entirely.
    // Parent wires onDelete to clear the draft without an API call.
    if (initialEditing) {
      onDelete?.(experience.id);
      return;
    }
    setForm(experience);
    setIsEditing(false);
  };

  const handleChange = (field: keyof TalentExperience, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  if (isEditing) {
    return (
      <div className="rounded-lg border border-light-lila/40 bg-light p-4 flex flex-col gap-3">
        <Input
          type="text"
          value={form.role}
          onChange={(e) => handleChange('role', e.target.value)}
          placeholder={t('detail.fields.rolePlaceholder')}
        />
        <Input
          type="text"
          value={form.company}
          onChange={(e) => handleChange('company', e.target.value)}
          placeholder={t('detail.fields.companyPlaceholder')}
        />
        <div className="flex gap-2">
          <div className="flex-1">
            <DatePicker
              value={form.startDate}
              onChange={(v) => handleChange('startDate', v)}
              displayFormat="MMM yyyy"
              placeholder={t('detail.fields.startDatePlaceholder')}
            />
          </div>
          <div className="flex-1">
            <DatePicker
              value={form.endDate}
              onChange={(v) => handleChange('endDate', v)}
              disabled={form.isOngoing}
              displayFormat="MMM yyyy"
              placeholder={t('detail.fields.endDatePlaceholder')}
            />
          </div>
        </div>
        <label className="flex items-center justify-end gap-2 text-sm text-dark-invirtus/80 cursor-pointer">
          {t('detail.fields.currentlyHere')}
          <input
            type="checkbox"
            checked={form.isOngoing}
            onChange={(e) => handleChange('isOngoing', e.target.checked)}
            className="rounded border-light-lila"
          />
        </label>
        <Textarea
          value={form.description ?? ''}
          onChange={(e) => handleChange('description', e.target.value)}
          placeholder={t('detail.fields.descriptionPlaceholder')}
          className="resize-none"
        />
        <div className="flex items-center justify-end gap-2 pt-1">
          <Button
            type="outlined"
            intent="secondary"
            size="sm"
            label={t('detail.cancelEdit')}
            onClick={handleDiscard}
          />
          <Button
            type="contained"
            intent="primary"
            size="sm"
            label={t('detail.saveSection')}
            onClick={handleSave}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-light-lila/40 bg-light p-4 flex gap-3 group">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <Building2 size={15} className="shrink-0 text-dark-invirtus/60" />
          <Typography variant="body-md" weight="semibold" color="primary">
            {experience.role}
          </Typography>
        </div>
        <Typography variant="body-sm" color="secondary" className="mt-0.5">
          {experience.company}
        </Typography>
        <div className="flex items-center gap-1.5 mt-0.5">
          <Calendar size={13} className="shrink-0 text-dark-invirtus/45" />
          <Typography variant="body-sm" color="muted">
            {formatDateRange(
              t('detail.exp.ongoing'),
              experience.startDate,
              experience.endDate,
              experience.isOngoing,
            )}
          </Typography>
        </div>
        {experience.description && (
          <Typography variant="body-sm" color="secondary" className="mt-2">
            {experience.description}
          </Typography>
        )}
      </div>
      <div className="flex items-start gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
        <Button
          type="icon"
          intent="muted"
          size="sm"
          icon={<Pencil />}
          title={t('detail.editExperience')}
          onClick={() => setIsEditing(true)}
        />
        <Button
          type="icon"
          intent="danger"
          size="sm"
          icon={<Trash2 />}
          title={t('detail.deleteExperience')}
          onClick={() => onDelete?.(experience.id)}
        />
      </div>
    </div>
  );
};

ExperienceCard.displayName = 'ExperienceCard';
